const GUIDED_PRACTICE_TYPE = 'Guided Practice';
const WORK_SAMPLE_TYPE = 'Work Sample';

export function isLearningPathwayReady(record) {
  if (!record || record.generationStatus === 'error') return false;
  if (record.generationStatus === 'completed') return true;
  const pathwayData = record.pathwayData;
  return Boolean(record.stage === 'completed'
    && pathwayData
    && typeof pathwayData === 'object'
    && Object.keys(pathwayData).length > 0);
}

function taskType(raw = {}, project = '') {
  const rawType = String(raw.type || raw.projectType || '').toLowerCase().replace(/[-_]+/g, ' ').trim();
  const title = String(project || raw.project || raw.title || raw.name || '').toLowerCase();
  if (['case study', 'work sample', 'skill challenge', 'transfer challenge'].includes(rawType)) return WORK_SAMPLE_TYPE;
  if (['exercise', 'guided practice', 'practice', 'mini project', 'applied project'].includes(rawType)) return GUIDED_PRACTICE_TYPE;
  if (rawType) return title.includes('case study') || title.includes('work sample') ? WORK_SAMPLE_TYPE : GUIDED_PRACTICE_TYPE;
  return title.includes('case study') || title.includes('work sample') ? WORK_SAMPLE_TYPE : GUIDED_PRACTICE_TYPE;
}

function normalizeExercise(raw = {}, fallbackTitle = 'Practice Task') {
  const project = raw.project || raw.title || raw.name || fallbackTitle;
  const type = taskType(raw, project);
  const displayProject = type === WORK_SAMPLE_TYPE
    ? String(project).replace(/\bcase study\b:?/gi, 'Work Sample').trim()
    : project;
  return {
    project: displayProject || fallbackTitle,
    type,
    description: raw.description || raw.instructions || '',
    browse_criteria: raw.browse_criteria || raw.browseCriteria || '',
  };
}

function defaultGuidedPractice(title) {
  return {
    project: `${title || 'Skill'} Guided Practice`,
    type: GUIDED_PRACTICE_TYPE,
    description: `Goal: Practice ${title || 'this skill'} with one small task. Steps: 1. Pick a simple work example. 2. Make one useful output. 3. Write down your steps and choices. What to make: Save your work with a short note about your steps. Checklist: - You used the skill. - Your steps are easy to follow. - You can finish the task in one sitting.`,
  };
}

function defaultWorkSample(title) {
  return {
    project: `${title || 'Skill'} Work Sample`,
    type: WORK_SAMPLE_TYPE,
    description: `Situation: Show how you would use ${title || 'this skill'} in a real job task. Your task: Solve one clear problem for your target job or industry. What to make: Save your work with a short README or one-page note. Checklist: - You can explain the work in an interview. - Your choices are easy to follow. - The work shows how you use the skill. Check your work: What would you improve before showing this to a hiring manager?`,
  };
}

function rawExercises(item = {}) {
  if (Array.isArray(item.learnAndApply)) return item.learnAndApply;
  if (Array.isArray(item.learn_and_apply)) return item.learn_and_apply;
  if (Array.isArray(item.projects)) return item.projects;
  if (Array.isArray(item.exercises)) return item.exercises;
  return [];
}

function learningExercises(item = {}, title = 'Learning step') {
  const raw = rawExercises(item);
  const normalized = raw.filter(Boolean).map((exercise) => normalizeExercise(exercise, `${title} Guided Practice`));
  const guidedPractice = normalized.find((exercise) => exercise.type !== WORK_SAMPLE_TYPE);
  const workSample = normalized.find((exercise) => exercise.type === WORK_SAMPLE_TYPE);
  return [
    guidedPractice || defaultGuidedPractice(title),
    workSample || defaultWorkSample(title),
  ];
}

function learningCourses(item = {}) {
  const raw = Array.isArray(item.topCourses) ? item.topCourses : [];
  return raw.filter((course) => {
    if (!course?.link) return false;
    try {
      const url = new URL(course.link);
      const host = url.hostname.toLowerCase().replace(/^www\./, '');
      const path = url.pathname.replace(/\/$/, '').toLowerCase() || '/';
      if ((host === 'google.com' || host.endsWith('.google.com')) && ['/search', '/url'].includes(path)) return false;
      if ((host === 'youtube.com' || host.endsWith('.youtube.com')) && path === '/results') return false;
      if ((host === 'bing.com' || host.endsWith('.bing.com') || host === 'search.yahoo.com') && path === '/search') return false;
      if ((host === 'duckduckgo.com' || host.endsWith('.duckduckgo.com')) && url.search) return false;
      return ['http:', 'https:'].includes(url.protocol);
    } catch (_error) {
      return false;
    }
  });
}

export function buildPathwaySteps(pathwayData = {}) {
  const steps = [];
  const skills = Array.isArray(pathwayData.skills) ? pathwayData.skills : [];
  const methodologies = Array.isArray(pathwayData.methodologies) ? pathwayData.methodologies : [];
  const digitalTools = pathwayData.digitalTools || null;

  skills.forEach((skill) => {
    const title = skill.skillTitle || skill.skill_title || 'Learning step';
    steps.push({
      type: 'skill',
      title,
      profileTag: skill.profileTag || 'Learning Pathway Badge',
      whatYouLearn: skill.whatYouLearn || '',
      whyYouShouldLearnIt: skill.whyYouShouldLearnIt || '',
      courses: learningCourses(skill),
      exercises: learningExercises(skill, title),
    });
  });

  methodologies.forEach((methodology) => {
    const title = methodology.theoryTitle || methodology.theory_title || 'Learning step';
    steps.push({
      type: 'methodology',
      title,
      profileTag: methodology.profileTag || 'Learning Pathway Badge',
      whatYouLearn: methodology.whatYouLearn || '',
      whyYouShouldLearnIt: methodology.whyYouShouldLearnIt || '',
      courses: learningCourses(methodology),
      exercises: learningExercises(methodology, title),
    });
  });

  if (digitalTools && Array.isArray(digitalTools.tools) && digitalTools.tools.length > 0) {
    steps.push({
      type: 'tools',
      title: 'Digital Tools to Learn',
      profileTag: 'Digital Tools Proficiency',
      whatYouLearn: digitalTools.whatYouLearn || '',
      whyYouShouldLearnIt: digitalTools.whyYouShouldLearnIt || '',
      courses: digitalTools.tools.map((tool) => ({
        course_name: tool.name,
        link: tool.url,
        pricing: 'Official',
        rating: 'Official',
      })),
      exercises: [],
      tools: digitalTools.tools,
    });
  }

  return steps.map((step, index) => ({ ...step, number: index + 1 }));
}

export function collectPathwayBadges(pathwayData = {}) {
  const badges = [];
  const skills = Array.isArray(pathwayData.skills) ? pathwayData.skills : [];
  const methodologies = Array.isArray(pathwayData.methodologies) ? pathwayData.methodologies : [];
  const tools = Array.isArray(pathwayData.digitalTools?.tools) ? pathwayData.digitalTools.tools : [];

  skills.forEach((skill) => {
    if (skill.profileTag) badges.push(cleanTag(skill.profileTag));
  });
  methodologies.forEach((methodology) => {
    if (methodology.profileTag) badges.push(cleanTag(methodology.profileTag));
  });
  tools.forEach((tool) => {
    if (tool.name) badges.push(tool.name);
  });

  return [...new Set(badges.filter(Boolean))];
}

export function normalizeCompletedPathways(rawCompleted) {
  if (!rawCompleted) return [];
  const entries = Array.isArray(rawCompleted)
    ? rawCompleted.map((value, index) => [String(index), value])
    : Object.entries(rawCompleted);

  return entries
    .map(([id, pathway]) => {
      const onboarding = pathway?.onboardingData || {};
      const completedSteps = Array.isArray(pathway?.completedSteps) ? pathway.completedSteps : [];
      return {
        id,
        targetJob: onboarding.targetJob || 'Learning pathway',
        industry: onboarding.industry || '',
        completedAt: pathway?.completedAt || pathway?.updatedAt || pathway?.generatedAt || 0,
        completedCount: completedSteps.length,
        totalSteps: pathway?.totalSteps || completedSteps.length,
        allBadges: collectPathwayBadges(pathway?.pathwayData || {}),
        pathway,
      };
    })
    .sort((left, right) => (right.completedAt || 0) - (left.completedAt || 0));
}

export function compactDate(timestamp, language = 'en') {
  if (!timestamp) return '';
  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-LB' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(timestamp));
}

export function getDisplayName(profile, user) {
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
  return fullName || profile?.username || user?.displayName || user?.email?.split('@')[0] || 'JOBSEEQR User';
}

export function cleanTag(raw = '') {
  const value = String(raw || '').trim();
  if (value.includes(' - ')) return value.split(' - ').pop().trim();
  return value;
}
